import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import stylisRTLPlugin from 'stylis-plugin-rtl';

import { muiTheme } from '../../mui-theme';
import {
  DEFAULT_LOCALE,
  htmlLangForLocale,
  isRtlLocale,
  localeFromPathname,
  messagesForLocale,
  setActiveLocale,
} from '../../util/locale';
import { IntlProvider } from '../../util/reactIntl';

const rtlCache = createCache({
  key: 'muirtl',
  stylisPlugins: [stylisRTLPlugin],
});

const ltrCache = createCache({
  key: 'mui',
});

/**
 * Provides Intl + MUI direction from the URL locale segment.
 * Must be rendered inside a Router.
 */
const LocaleAwareProviders = props => {
  const {
    children,
    enMessages,
    arMessages,
    hostedTranslations = {},
    fallbackLocale = DEFAULT_LOCALE,
  } = props;
  const location = useLocation();
  const locale = localeFromPathname(location.pathname) || fallbackLocale;
  const rtl = isRtlLocale(locale);

  useEffect(() => {
    setActiveLocale(locale);
    if (typeof document !== 'undefined') {
      const html = document.documentElement;
      html.setAttribute('lang', htmlLangForLocale(locale));
      html.setAttribute('dir', rtl ? 'rtl' : 'ltr');
    }
  }, [locale, rtl]);

  const messages = useMemo(
    () =>
      messagesForLocale(locale, {
        enMessages,
        arMessages,
        hostedTranslations,
      }),
    [locale, enMessages, arMessages, hostedTranslations]
  );

  const theme = useMemo(
    () =>
      createTheme({
        ...muiTheme,
        direction: rtl ? 'rtl' : 'ltr',
      }),
    [rtl]
  );

  const cache = rtl ? rtlCache : ltrCache;

  return (
    <IntlProvider locale={locale} messages={messages} textComponent="span">
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>{children}</ThemeProvider>
      </CacheProvider>
    </IntlProvider>
  );
};

LocaleAwareProviders.propTypes = {
  children: PropTypes.node.isRequired,
  enMessages: PropTypes.object.isRequired,
  arMessages: PropTypes.object.isRequired,
  hostedTranslations: PropTypes.object,
  fallbackLocale: PropTypes.string,
};

export default LocaleAwareProviders;
